import { IonContent } from '@ionic/react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { UiButtonBase } from '@/components/ui/ButtonBase';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount, useUserStore } from '@/hooks/useAccount';
import { apiV1PostStripeClient } from '@/modules/api';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { userService } from '@/services/user';
import { getFbCookie } from '@/utils/getFbCookie';
import { getGaCookie } from '@/utils/getGaCookie';

import { OwnAiTitle } from '../OwnAI/components/Title';

import { LayoutStyles } from './Devmode.styles';
import { LayoutFormDataType } from './types';

const LayoutDevmode: React.FC = () => {
  const user = useUserStore((state) => state.user);
  const { refreshUser } = useAccount();

  const form = useForm<LayoutFormDataType>({
    defaultValues: {
      email: user?.email || '',
      client_secret: user?.onboarding_data?.devmode_client_secret || '',
    },
  });

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: apiV1PostStripeClient,
    mutationKey: [apiV1PostStripeClient.requestKey],
  });

  const onSubmit = async (data: LayoutFormDataType) => {
    const { fbc, fbp } = getFbCookie();
    const { gaUserPseudoId } = getGaCookie();
    await Promise.all([
      mutateAsync({
        email: data.email,
      }),
      userService.createOrUpdate({
        onboarding_data: {
          onboarded: true,
          ...(data.client_secret !== undefined && {
            devmode_client_secret: data.client_secret?.trim() || null,
          }),
        },
        ...(fbc && { fbc }),
        ...(fbp && { fbp }),
        ...(gaUserPseudoId && { ga_user_pseudo_id: gaUserPseudoId }),
      }),
    ]);

    refreshUser();
  };

  return (
    <LayoutStyles>
      <IonContent>
        <OwnAiTitle>Devmode Layout</OwnAiTitle>

        <form className="form-devsub" onSubmit={form.handleSubmit(onSubmit)}>
          <UiTypography component="label" htmlFor="email">
            Email
            <input
              type="text"
              id="email"
              {...form.register('email', {
                required: {
                  value: true,
                  message: 'This field is required',
                },
                validate: (value) => {
                  if (!value) {
                    return 'This field is required';
                  }

                  if (!checkIsValidateEmail(value)) {
                    return 'Invalid email address';
                  }

                  return true;
                },
              })}
            />
          </UiTypography>

          <UiTypography component="label" htmlFor="client_secret">
            Client secret (optional)
            <input
              type="text"
              id="client_secret"
              placeholder="pi_xxx_secret_xxx"
              {...form.register('client_secret')}
            />
          </UiTypography>

          {form.formState.errors.email && (
            <UiTypography variant="body-small" color="danger">
              {form.formState?.errors?.email?.message || 'Error'}
            </UiTypography>
          )}

          <UiButtonBase
            expand="full"
            type="submit"
            disabled={isLoading}
            pending={isLoading}
          >
            Submit
          </UiButtonBase>
        </form>
      </IonContent>
    </LayoutStyles>
  );
};

export default LayoutDevmode;
